import React from 'react';
import { getStorage } from 'firebase/storage';
import SitesListBase from '../../site/SitesList';
import {
  Case,
  Site,
  updateExistingSite,
} from '../../../../services/buildfire/rdb/cases';
import { useFirebase } from '../../../../services/firebase';
import * as Storage from '../../../../services/firebase/storage';
import ModalLoader from '../../../common/ModalLoader';
import CaseContext from '../../../../services/buildfire/rdb/CaseContext';
import { getDatabase } from 'firebase/database';
import { useIonToast } from '@ionic/react';
import { warning } from 'ionicons/icons';
import useGoBackToCase from '../../../../hooks/useGoBackToCase';

interface SitesListProps {
  case: Case;
  sites: Site[];
  userId: string;
}

function calculatePercent(num: number, total: number) {
  return Math.min((num + 1) / total, 0.99);
}

const SitesList: React.FC<SitesListProps> = (props) => {
  const { app, currentUser } = useFirebase();
  const [progress, setProgress] = React.useState<{
    message: string;
    percent: number;
  } | null>(null);
  const { updateCase: updateCaseLocally } = React.useContext(CaseContext);
  const [present] = useIonToast();

  return (
    <>
      <SitesListBase
        {...props}
        isEditable
        onEdited={async (site, index) => {
          try {
            console.log('Editing site:', site, index);
            setProgress({
              message: 'Uploading site files...',
              percent: 0,
            });

            const storage = getStorage(app);
            const uploadedSites = await Storage.uploadSites(
              storage,
              [site],
              currentUser!.uid,
              (num, total, name, siteIndex) => {
                const siteNum = siteIndex + 1;
                const numTotalAttachments = site.images.filter((img) => {
                  if (img instanceof File) return true;
                  if (typeof img === 'object' && 'status' in img && img.status === 'failed' && img.originalFile) {
                    return true;
                  }
                  return false;
                }).length;
                const message = `Uploaded ${
                  num + 1
                } of ${numTotalAttachments} files (site #${siteNum})`;
                const percent = calculatePercent(num, numTotalAttachments);
                setProgress({ message, percent });
              }
            );

            const newSite = uploadedSites.sites[0];

            // Only update database if all uploads succeeded
            if (!uploadedSites.hasFailures) {
              const db = getDatabase(app);
              await updateExistingSite(db, props.case.id, index, newSite);

              updateCaseLocally({
                ...props.case,
                sites: props.case.sites.map((s, i) =>
                  i === index ? newSite : s
                ),
              });

              present({
                message: 'Site updated successfully.',
                duration: 5000,
              });
            } else {
              present({
                message: `Failed to upload ${uploadedSites.failedCount} image(s). Please try again.`,
                duration: 5000,
                color: 'danger',
                icon: warning,
              });
            }

            setProgress(null);

            // Return result so modal knows whether to close
            return {
              hasFailures: uploadedSites.hasFailures,
              site: newSite,
            };
          } catch (e) {
            console.error(e);
            setProgress(null);
            present({
              message: 'Failed to update site. Please try again.',
              duration: 5000,
              color: 'danger',
              icon: warning,
            });
            // Return failure result
            return { hasFailures: true };
          }
        }}
      />

      {progress && (
        <ModalLoader
          ProgressBarProps={{
            indeterminate: !progress?.percent,
            progress: progress?.percent,
          }}
          progressText={progress?.message}
          onClose={() => {}}
        />
      )}
    </>
  );
};

export default SitesList;
